import { AssignedUser } from '@deltek/specification-client/core/Models/listing'
import { User } from '@deltek/specification-client/core/Models/user'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { truncateString } from '@deltek/specification-client/core/Utilities/stringUtils'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { makeStyles, Theme } from '@material-ui/core'
import Button from '@material-ui/core/Button'
import Divider from '@material-ui/core/Divider'
import Popover from '@material-ui/core/Popover'
import Typography from '@material-ui/core/Typography'
import React, { ChangeEvent } from 'react'
import AssignedUserList from './AssignedUserList'
import AutoCompleteAssignMent from './AutocompleteAssignment'

export interface StyleProps {
    emptyButtonState: boolean
}

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        width: '100%',
        maxWidth: 360,
        backgroundColor: theme.palette.background.paper,
        position: 'relative',
        overflow: 'auto',
        maxHeight: 300,
    },
    listSection: {
        backgroundColor: 'inherit',
    },
    ul: {
        backgroundColor: 'inherit',
        padding: 0,
    },
    filterButton: {
        backgroundColor: '#D9F1F0',
        border: '2px solid #02A09C',
        borderRadius: 100,
        height: 38,
    },
    filterButtonEmptySelection: {
        backgroundColor: theme.palette.common.white,
        border: '1px solid #02A09C',
        borderRadius: 100,
        height: 38,
    },
    filterButtonOpen: {
        backgroundColor: '#24CEAD',
        border: '1px solid #02A09C',
        borderRadius: 100,
        height: 38,
    },
    popOverBox: {},
    paper: {
        overflowX: 'unset',
        overflowY: 'unset',
        borderRadius: 10,
        '&::before': {
            content: '""',
            position: 'absolute',
            marginRight: '-0.71em',
            top: 0,
            left: 90,
            width: 15,
            height: 15,
            backgroundColor: '#5E5F61',
            boxShadow: theme.shadows[1],
            transform: 'translate(50%, -50%) rotate(-45deg)',
            clipPath:
                'polygon(-5px -5px, calc(100% + 5px) -5px, calc(100% + 5px) calc(100% + 5px))',
        },
    },
    filterButtonText: {
        fontStyle: 'normal',
        fontWeight: 500,
        fontSize: '14px',
        lineHeight: '16px',
        color: '#202020',
        textTransform: 'none',
    },
    filterButtonTextEmptySelection: {
        fontStyle: 'normal',
        fontWeight: 'normal',
        fontSize: '14px',
        lineHeight: '16px',
        color: '#5E5F61',
        textTransform: 'none',
    },
    filterButtonTextOpen: {
        fontStyle: 'normal',
        fontWeight: 500,
        fontSize: '14px',
        lineHeight: '16px',
        color: '#FFFFFF',
        textTransform: 'none',
    },
    filterBox: {
        width: 360,
        height: 486,
        border: '1px solid #C9C9C9',
        boxShadow: '4px 4px 20px rgba(0, 0, 0, 0.3)',
        borderRadius: 5,
    },
    autoHeight: {
        height: 'auto',
    },
    filterBoxHeaderTextContainer: {
        height: 56,
        padding: 14,
    },
    filterBoxAutocompleteContainer: {
        padding: 14,
        flex: 1,
    },
    filterBoxBottomButtonsContainer: {
        padding: 14,
        height: 71,
    },
    filterBoxHeaderText: {
        fontStyle: 'normal',
        fontWeight: 500,
        fontSize: pxToRem(20),
        lineHeight: '25px',
        color: '#047A7D',
        textTransform: 'uppercase',
    },
    divider: {
        backgroundColor: '#000000',
        height: 2,
    },
    clearButton: {
        border: '1px solid #5E5F61',
        textTransform: 'none',
        borderRadius: 100,
        height: 38,
        paddingLeft: 20,
        paddingRight: 20,
        marginRight: 9,
    },
    closeButton: {
        textTransform: 'none',
        background: '#04797C',
        '&:hover': {
            background: '#036063',
        },
        fontWeight: 'normal',
        borderRadius: 100,
        height: 38,
        paddingLeft: 20,
        paddingRight: 20,
    },
    button: {
        padding: theme.spacing(0.75, 2),
        width: 'auto',
        minWidth: (props: StyleProps) =>
            props.emptyButtonState ? pxToRem(150) : pxToRem(250),
        height: 41,
        backgroundColor: '#5E5F61',
        color: '#e2e2e2',
        borderRadius: 100,
        ...theme.typography.h6,
        '&:hover': {
            backgroundColor: '#5E5F61',
        },
    },
    buttonOpen: {
        backgroundColor: '#5E5F61',
        borderRadius: 100,
        ...theme.typography.h6,
        width: 'auto',
        minWidth: (props: StyleProps) =>
            props.emptyButtonState ? 'auto' : pxToRem(250),
        height: 41,
    },
    buttonText: {
        fontSize: pxToRem(18),
        lineHeight: pxToRem(16),
        color: theme.palette.common.white,
    },
    stateButton: {
        lineHeight: '1.95rem',
    },
    notEditableButton: {
        '&:hover': {
            cursor: 'pointer',
        },
    },
    buttonTextOpen: {
        fontSize: pxToRem(18),
        lineHeight: pxToRem(16),
        color: theme.palette.common.white,
    },
}))
export interface PopupButtonProps {
    /**
     * Text to show in the selection box (e..g, "Select a value")
     */
    selectPlaceholder?: string

    /**
     * Filter button name (needed to link the popover to the filter button.)
     */
    name: string

    /**
     * Values to show in the filter list.
     */
    items: Partial<User>[]

    /**
     * Callback fired when the selection changes.
     */
    onChange?: (
        /** Event source */
        event: ChangeEvent<{}>,
        /** New selection */
        value: Partial<AssignedUser>[]
    ) => void

    /** Callback fired shortly after the input text changes. */
    onInputChangeDebounced?: (value: string) => void

    /**
     * Items to show as selected.
     */
    selectedItems?: Partial<AssignedUser>[] | null

    /** If displayed pill and on hover are different, we can control it by passing the same set of modified array here.
     * For Family Names, the displayed value in the pill has no sectionNumber compared to the value on hover which has
     */
    selectedDisplayItems?: string[]

    /** Items to show in the list as disabled/non-selectable. */
    disabledItems?: string[]

    /** Fired when user opens the autocomplete drop-down. */
    onOpenDropDown?: (event: ChangeEvent<{}>) => void

    /** Fired when user closes the autocomplete drop-down. */
    onCloseDropDown?: (event: ChangeEvent<{}>) => void

    /**
     * Fired when the filter popover is closed, either due to a "Display result" button click,
     * or closing the popover some other way such as clicking outside of it.
     */
    onClose?: () => void

    /** If true, show a loading state for the Autocomplete drop-down listbox. */
    loading?: boolean

    /** If true, show custom format for option. */
    customFormat?: boolean
    isPopoverOpen: boolean
    isOnEditMode: boolean
    isDeltekAdmin: boolean
    canEdit: boolean
    buttonVariant: 'BPM' | 'Deltek'
}

/**
 * Component with a button that, when clicked, displays a popover with an Autocomplete multi-select.
 */
const PopupButton: React.FC<PopupButtonProps> = ({
    selectPlaceholder,
    name,
    items,
    selectedItems = [],
    selectedDisplayItems = [],
    onOpenDropDown,
    onChange,
    onInputChangeDebounced,
    onClose,
    loading = false,
    customFormat = false,
    isOnEditMode,
    isDeltekAdmin,
    canEdit,
    buttonVariant,
}) => {
    const isSelectionEmpty = !selectedItems || !selectedItems.length
    const classes = useStyles({ emptyButtonState: isSelectionEmpty })
    const emptyArray: Partial<AssignedUser>[] = []

    const [
        popoverAnchorEl,
        setPopoverAnchorEl,
    ] = React.useState<HTMLButtonElement | null>(null)
    const [openList, handleOpenList] = React.useState<boolean>(false)
    const [isPopoverOpen, handlePopoverOpen] = React.useState<boolean>(false)

    const popoverId = React.useMemo(
        () => (isPopoverOpen ? `${name}-popover` : undefined),
        [isPopoverOpen, name]
    )
    const fullSelectedName = React.useMemo(
        () =>
            selectedItems && selectedItems.length > 0
                ? `${selectedItems[0]?.profileInfo?.firstName} ${selectedItems[0]?.profileInfo?.lastName}`
                : '',
        [selectedItems]
    )
    const truncatedDisplayName = React.useMemo(() => {
        const characterLimit =
            selectedItems && selectedItems.length > 1 ? 10 : 20
        return truncateString(fullSelectedName, characterLimit)
    }, [fullSelectedName, selectedItems])

    const labelText = React.useMemo(
        () =>
            selectedItems && selectedItems.length > 0
                ? selectedItems.length === 1
                    ? truncatedDisplayName
                    : `${truncatedDisplayName}(${
                          selectedItems.length - 1
                      } more...)`
                : 'None',
        [selectedItems, truncatedDisplayName]
    )
    let autocompleteInputEl: HTMLElement
    const onInputRef = (inputElement: HTMLElement) => {
        autocompleteInputEl = inputElement
    }
    const handleFilterButtonClick = (
        event: React.MouseEvent<HTMLButtonElement>
    ) => {
        if (isOnEditMode) {
            if (buttonVariant === 'Deltek') {
                if (isDeltekAdmin) {
                    handleOpenList(false)
                    setPopoverAnchorEl(event.currentTarget)
                    handlePopoverOpen(true)
                }
            } else {
                if (canEdit || isDeltekAdmin) {
                    handleOpenList(false)
                    setPopoverAnchorEl(event.currentTarget)
                    handlePopoverOpen(true)
                }
            }
        }
    }
    const handleFilterButtonHover = (
        event: React.MouseEvent<HTMLButtonElement>
    ) => {
        if (!isOnEditMode && selectedItems?.length !== 0) {
            handleOpenList(true)
            handlePopoverOpen(true)
            setPopoverAnchorEl(event.currentTarget)
        }
    }

    const handlePopoverClose = () => {
        setPopoverAnchorEl(null)

        handleOpenList(false)
        handlePopoverOpen(false)

        onClose?.()
    }

    const handleChange = (
        event: ChangeEvent<{}>,
        value: Partial<AssignedUser>[]
    ) => {
        onChange?.(event, value)
    }

    const handleClearButtonClick = (
        event: React.MouseEvent<HTMLButtonElement>
    ) => {
        onChange?.(event, emptyArray)
        autocompleteInputEl && autocompleteInputEl.focus()
    }
    const closeDropDown = () => {
        handleOpenList(false)
        handlePopoverOpen(false)
    }
    return (
        <>
            <Button
                disableElevation
                onMouseEnter={handleFilterButtonHover}
                disableRipple
                className={classes.button}
                aria-describedby={popoverId}
                variant="contained"
                onClick={handleFilterButtonClick}
                data-testid="filter-button"
            >
                {labelText}
            </Button>
            <Popover
                id={popoverId}
                anchorEl={popoverAnchorEl}
                open={isPopoverOpen}
                onClose={handlePopoverClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: -14,
                    horizontal: 'left',
                }}
                onEntered={() => {
                    autocompleteInputEl && autocompleteInputEl.focus()
                }}
                classes={{
                    paper: !openList && isOnEditMode ? '' : classes.paper,
                }}
                marginThreshold={7}
            >
                {!openList && isOnEditMode ? (
                    <GridContainer
                        direction="column"
                        className={classes.filterBox}
                    >
                        <GridItem
                            className={classes.filterBoxHeaderTextContainer}
                        >
                            <Typography className={classes.filterBoxHeaderText}>
                                ASSIGN {buttonVariant}
                            </Typography>
                        </GridItem>
                        <GridItem>
                            <Divider className={classes.divider} />
                        </GridItem>
                        <GridItem
                            className={classes.filterBoxAutocompleteContainer}
                        >
                            {
                                <AutoCompleteAssignMent
                                    name={`${name}-autocomplete`}
                                    items={
                                        buttonVariant === 'BPM'
                                            ? items
                                            : items?.filter((item) => {
                                                  return (
                                                      item.role?.toLowerCase() !==
                                                      'onboarder'
                                                  )
                                              })
                                    }
                                    placeholder={
                                        isSelectionEmpty
                                            ? selectPlaceholder
                                            : ''
                                    }
                                    onChange={handleChange}
                                    onInputChangeDebounced={
                                        onInputChangeDebounced
                                    }
                                    onOpen={onOpenDropDown}
                                    onClose={closeDropDown}
                                    selectedItems={selectedItems}
                                    selectedDisplayItems={selectedDisplayItems}
                                    onInputRef={onInputRef}
                                    loading={loading}
                                    customFormat={customFormat}
                                />
                            }
                        </GridItem>
                        <GridItem>
                            <Divider className={classes.divider} />
                        </GridItem>
                        <GridItem
                            container
                            className={classes.filterBoxBottomButtonsContainer}
                            direction="row"
                            justifyContent="flex-end"
                        >
                            {!openList && (
                                <Button
                                    disableElevation
                                    data-testid="clear-button"
                                    className={classes.clearButton}
                                    onClick={handleClearButtonClick}
                                >
                                    Clear
                                </Button>
                            )}
                            <Button
                                disableElevation
                                variant="contained"
                                color="primary"
                                data-testid="close-button"
                                className={classes.closeButton}
                                onClick={handlePopoverClose}
                            >
                                Close
                            </Button>
                        </GridItem>
                    </GridContainer>
                ) : (
                    <AssignedUserList
                        items={selectedItems ?? []}
                        buttonVariant={buttonVariant}
                    />
                )}
            </Popover>
        </>
    )
}

export default PopupButton
